import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, BookOpen, ClipboardCheck, GraduationCap, RefreshCw, Trophy } from 'lucide-react'
import login_banner from '../../../assets/images/banner.png'
import { LoginFormImport } from '../../../components/forms/user_login/Import'
import { ForgotPasswordPanel } from '../../../components/forms/user_login/UserLoginForm'
import { isStudentRole } from '../../../utils/isStudent'
const { RoutesMap, logo, useFormik, LoginSchema, useNavigate, useDispatch, toast, KeyRound, UserRound, Eye, EyeOff, ButtonLoader, SessionManager, signIn, sidebar } = LoginFormImport

const CAPTCHA_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

function makeCaptcha(length = 5) {
    let code = ''
    for (let i = 0; i < length; i += 1) {
        code += CAPTCHA_CHARS[Math.floor(Math.random() * CAPTCHA_CHARS.length)]
    }
    return code
}

function drawCaptcha(canvas, code) {
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const { width, height } = canvas
    ctx.clearRect(0, 0, width, height)
    ctx.fillStyle = '#f4f7fb'
    ctx.fillRect(0, 0, width, height)

    for (let i = 0; i < 18; i += 1) {
        ctx.fillStyle = `rgba(80, 110, 228, ${0.15 + Math.random() * 0.35})`
        ctx.beginPath()
        ctx.arc(Math.random() * width, Math.random() * height, 1.2, 0, Math.PI * 2)
        ctx.fill()
    }

    for (let i = 0; i < 4; i += 1) {
        ctx.strokeStyle = i % 2 ? 'rgba(227, 28, 35, 0.35)' : 'rgba(80, 110, 228, 0.35)'
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(Math.random() * width, Math.random() * height)
        ctx.lineTo(Math.random() * width, Math.random() * height)
        ctx.stroke()
    }

    ctx.textBaseline = 'middle'
    ctx.textAlign = 'center'
    ctx.font = '700 28px Arial'
    const step = width / (code.length + 1)
    ;[...code].forEach((char, index) => {
        ctx.save()
        ctx.translate(step * (index + 1), height / 2 + (Math.random() * 6 - 3))
        ctx.rotate((Math.random() - 0.5) * 0.55)
        ctx.fillStyle = index % 2 ? '#001529' : '#506EE4'
        ctx.fillText(char, 0, 0)
        ctx.restore()
    })
}

export default function StudentLogin() {

    const [spinner, setSpinner] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [captcha, setCaptcha] = useState(makeCaptcha)
    const [captchaInput, setCaptchaInput] = useState('')
    const [captchaError, setCaptchaError] = useState('')
    const [showForgot, setShowForgot] = useState(false)
    const captchaRef = useRef(null)

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const resetCaptcha = () => {
        setCaptcha(makeCaptcha())
        setCaptchaInput('')
    }

    useEffect(() => {
        if (captchaRef.current) drawCaptcha(captchaRef.current, captcha)
    }, [captcha, showForgot])

    const initialValues = {
        phone: '',
        password: '',
    };

    const { values, errors, touched, handleBlur, handleChange, handleSubmit } = useFormik({
        initialValues: initialValues,
        validationSchema: LoginSchema,

        onSubmit: (values) => {
            if (captchaInput.trim() !== captcha) {
                setCaptchaError('Enter the captcha correctly')
                resetCaptcha()
                return
            }
            setCaptchaError('')
            setSpinner(true)
            dispatch(signIn(values)).then((res) => signIn_user(res.payload))
        },
    })

    const show_error = (res) => {
        setSpinner(false)
        if (res?.status === 400) {
            toast.error(res?.data?.message, { position: "bottom-right" })
        } else if (res?.status === 500) {
            toast.error("Server error!!..", { position: "bottom-right" })
        } else {
            toast.error("Something went wrong!!..", { position: "bottom-right" })
        }
    }

    const signIn_user = (res) => {

        if (res?.status === 200) {

            // Only students may sign in from this page
            if (!isStudentRole(res?.data?.data?.user)) {
                setSpinner(false)
                toast.error("This login is only for students. Please use the staff / admin login.", { position: "bottom-right" })
                return
            }

            SessionManager.setCookie('user_token', `Bearer ${res?.data?.data?.token}`, 3)
            SessionManager.setCookie('user', res?.data?.data && JSON.stringify(res?.data?.data?.user), 3)

            toast.success("SignIn Successfully!!..", { position: "bottom-right" })

            const loggedInUser = res?.data?.data?.user
            if (['pending', 'hold'].includes(String(loggedInUser?.payment_status || '').toLowerCase())) {
                navigate(RoutesMap.PAYMENT.path)
                setSpinner(false)
                return
            }

            dispatch(sidebar({ navigate })).then((res1) => sidebar_res(res1.payload))

        } else {
            show_error(res)
        }
    }

    const sidebar_res = (res) => {
        if (res?.status === 200) {
            const permission = res?.data?.data?.attributes?.map(val => JSON.parse(val)).flat()
            SessionManager.setSession('permission', JSON.stringify(permission))
            navigate(RoutesMap.STUDENT_DASHBOARD.path)
            setSpinner(false)
        } else {
            show_error(res)
        }
    }

    const chips = [
        { icon: BookOpen, label: 'Your courses', position: 'one' },
        { icon: ClipboardCheck, label: 'Exams & assignments', position: 'two' },
        { icon: Trophy, label: 'Track progress', position: 'three' },
    ]

    return (
        <div className="stu-login-page">

            <form key={showForgot ? 'forgot' : 'login'} className="stu-login-form stu-login-from-right" onSubmit={(e) => { e.preventDefault(); if (!showForgot) handleSubmit(); }}>
                <div className="stu-login-form-body">
                    <img src={logo} alt="logo" className="stu-login-logo" />

                    <span className="stu-login-pill"><GraduationCap size={16} /> Student Portal</span>
                    {showForgot ? (
                        <ForgotPasswordPanel variant="student" onCancel={() => setShowForgot(false)} />
                    ) : (
                    <>
                    <h3>Sign In to Continue</h3>
                    <p className="stu-login-sub">Welcome back! Enter your details to start learning.</p>

                    <div className="stu-login-group">
                        <label htmlFor="stu-email">Email</label>
                        <div className={`stu-login-field ${errors.phone && touched.phone ? 'invalid' : ''}`}>
                            <UserRound size={18} />
                            <input
                                id="stu-email"
                                placeholder="Enter Your Email"
                                name='phone'
                                autoComplete="username"
                                value={values.phone || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </div>
                        {errors.phone && touched.phone ? (<span className='field_error'>{errors.phone}</span>) : null}
                    </div>

                    <div className="stu-login-group">
                        <label htmlFor="stu-password">Password</label>
                        <div className={`stu-login-field ${errors.password && touched.password ? 'invalid' : ''}`}>
                            <KeyRound size={18} />
                            <input
                                id="stu-password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Enter Your Password"
                                name='password'
                                autoComplete="current-password"
                                value={values.password || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <button type="button" className="stu-login-eye" onClick={() => setShowPassword(!showPassword)} aria-label="Toggle password">
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {errors.password && touched.password ? (<span className='field_error'>{errors.password}</span>) : null}
                    </div>

                    <div className="stu-login-group">
                        <label htmlFor="stu-captcha">Captcha</label>
                        <div className="stu-login-captcha">
                            <canvas ref={captchaRef} width={168} height={56} aria-label="Captcha image" />
                            <button type="button" className="stu-login-captcha-refresh" onClick={resetCaptcha} aria-label="Refresh captcha">
                                <RefreshCw size={18} />
                            </button>
                            <div className={`stu-login-field ${captchaError ? 'invalid' : ''}`}>
                                <input
                                    id="stu-captcha"
                                    placeholder="Enter captcha"
                                    name="captcha"
                                    autoComplete="off"
                                    value={captchaInput}
                                    onChange={(e) => { setCaptchaInput(e.target.value); setCaptchaError('') }}
                                />
                            </div>
                        </div>
                        {captchaError ? (<span className='field_error'>{captchaError}</span>) : null}
                        <div className="stu-login-forgot-row">
                            <button type="button" className="stu-login-forgot-link" onClick={() => setShowForgot(true)}>
                                Forgot password?
                            </button>
                        </div>
                    </div>

                    <button type='submit' className='stu-login-btn' disabled={spinner}>
                        {spinner ? <ButtonLoader /> : <>Login <ArrowRight size={18} /></>}
                    </button>
                    </>
                    )}

                    {/* <div className="stu-login-alt">
                        Not a student? <Link to={RoutesMap.LOGIN.path}>Staff / Admin login</Link>
                    </div> */}
                </div>

                <p className="stu-login-terms">By continuing, you agree to the <u>terms of service</u> and <u>privacy policy</u>.</p>
            </form>

            <div className="stu-login-side">
                <div className="stu-login-side-top">
                    <img src="./Rexoapp-Logo.png" alt="Rexoapp" />
                    <h6>Welcome to IQB LMS</h6>
                </div>

                <div className="stu-login-art">
                    <h1>Learn, practise and <span>grow.</span></h1>
                    <div className="stu-login-banner">
                        <img src={login_banner} alt="" />
                        {chips.map(({ icon: Icon, label, position }) => (
                            <span className={`stu-login-chip ${position}`} key={label}>
                                <i><Icon size={15} /></i>
                                {label}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="stu-login-side-foot">
                    <span>© Copyright 2025 - IQB LMS. All Right Reserved</span>
                    <span>Developed by <u onClick={() => window.open('https://www.techlene.com/', '_blank')}>Techlene Software Solutions Pvt. Ltd.</u></span>
                </div>
            </div>

        </div>
    )
}
